/**
 * When a parent can still book, move or cancel a meeting slot.
 *
 * A round can carry `lock_hours_before`: from that many hours before a slot starts, parents can
 * no longer book it, move into or out of it, or cancel it — the teacher has planned around the
 * list and a Google Meet invite has gone out. NULL means no lock, which is how every round
 * created before the setting existed behaves.
 *
 * The database enforces the same rule (`enforce_signup_change_lock()`), reading the slot's start
 * as an America/Los_Angeles wall clock time. These helpers read it the same way, so the page
 * never offers a button the database will refuse. Admins are exempt in the database; the parent
 * pages do not special-case them.
 */

import { BUSINESS_TIMEZONE, businessLocalToInstant } from './date';

export interface TimedSlot {
  /** `DATE`, "YYYY-MM-DD". */
  slot_date: string;
  /** `TIME`, "HH:MM[:SS]", business-local. */
  start_time: string;
}

/** The admin form's starting value for a new round. */
export const DEFAULT_LOCK_HOURS = 24;

/** Same bounds as the column's CHECK constraint. */
export const MAX_LOCK_HOURS = 720;

export function slotStartsAt(slot: TimedSlot): Date | null {
  return businessLocalToInstant(slot.slot_date, slot.start_time);
}

/** The moment parents stop being able to change this slot, or null when the round has no lock. */
export function slotLockDeadline(slot: TimedSlot, lockHoursBefore: number | null | undefined): Date | null {
  if (lockHoursBefore == null) return null;
  const start = slotStartsAt(slot);
  return start ? new Date(start.getTime() - lockHoursBefore * 3_600_000) : null;
}

export function isSlotLocked(
  slot: TimedSlot,
  lockHoursBefore: number | null | undefined,
  now: Date = new Date(),
): boolean {
  const deadline = slotLockDeadline(slot, lockHoursBefore);
  return deadline !== null && now.getTime() >= deadline.getTime();
}

export function hasSlotStarted(slot: TimedSlot, now: Date = new Date()): boolean {
  const start = slotStartsAt(slot);
  return start !== null && now.getTime() >= start.getTime();
}

/**
 * Whether a parent can pick this slot now. A slot that has already started is never offered,
 * lock or no lock — booking yesterday's meeting is not something anybody means to do.
 */
export function isSlotBookable(
  slot: TimedSlot,
  lockHoursBefore: number | null | undefined,
  now: Date = new Date(),
): boolean {
  return !hasSlotStarted(slot, now) && !isSlotLocked(slot, lockHoursBefore, now);
}

/** "24 hours", "1 hour", "2 days". */
export function describeLockWindow(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/** "Sun, Oct 18, 9:00 AM", in the business timezone. */
export function formatLockDeadline(deadline: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(deadline);
}

/** True for the trigger's refusal, so the page can show its words rather than a generic error. */
export function isSignupLockError(error: { message?: string } | null | undefined): boolean {
  return /locked for changes/i.test(error?.message ?? '');
}

/**
 * The column value for a form's lock fields. Off, blank or not a whole number in range is no
 * lock — the column's NULL — rather than a guess.
 */
export function toLockColumn(enabled: boolean, hours: number): number | null {
  if (!enabled || !Number.isInteger(hours) || hours < 0 || hours > MAX_LOCK_HOURS) return null;
  return hours;
}
